import { useEffect } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import {
  CertificatesContexts,
  getVesselCertificates,
  type VesselTypeCode,
  type VRCertificateType,
} from "@constants/certificates";
import {
  FORM_FIELDS,
  generateCertificateField,
  getAllCertificateFields,
} from "@features/vessel-register/utils";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { VesselOutletContext } from "../VesselRegistrationContainer";
import SingleCertificate from "./SingleCertificate";

const LANG_CERTS_BASE = "vessel-management.certificates";

const VesselCertificates = () => {
  const { formData, updateFormData, watch } = useOutletContext<VesselOutletContext>();
  const filesByField = useUploadStore((state) => state.filesByKey);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const vesselTypeCode = useWatch({
    control: formData.control,
    name: FORM_FIELDS.VESSEL_TYPE_CODE,
  }) as string | undefined;

  const grt = watch(FORM_FIELDS.GROSS_TONNAGE);

  const listOfCertificates = getVesselCertificates(
    CertificatesContexts.BASE,
    vesselTypeCode as VesselTypeCode,
    grt,
  );

  useEffect(() => {
    const store = useUploadStore.getState();

    listOfCertificates.forEach(({ type }) => {
      if (store.getFilesForKey(type).length > 0) return;

      const urlKey = generateCertificateField.url(type);
      const documentNameKey = generateCertificateField.documentName(type);
      const existingUrl = formData.getValues(urlKey);
      const existingName = formData.getValues(documentNameKey);

      if (!existingUrl) return;

      const entry: UploadEntry = {
        name:
          typeof existingName === "string" && existingName.trim().length > 0
            ? existingName
            : (existingUrl.split("/").pop() ?? type),
        url: existingUrl,
        certificateType: type,
      };

      store.setFilesForKey(type, [entry]);
    });
  }, [formData, listOfCertificates]);

  const removeFile = async (certType: VRCertificateType) => {
    const fields = getAllCertificateFields(certType);

    formData.setValue(fields.url, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(fields.documentName, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(fields.className, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    formData.setValue(fields.expiryDate, null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(fields.permanent, false, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    updateFormData(fields.url, "");
    updateFormData(fields.documentName, "");
    updateFormData(fields.className, "");
    updateFormData(fields.expiryDate, null);
    updateFormData(fields.permanent, false);

    formData.clearErrors(fields.className);
    formData.clearErrors(fields.expiryDate);
    await formData.trigger(fields.url);

    clearFilesForKey(certType);
  };

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();

  return (
    <div data-testid="vessels-certificate-page" className="flex flex-col gap-8 pb-8">
      <FormProvider {...formData}>
        {listOfCertificates.map((certificate) => {
          const name = certificate.type;

          return (
            <div key={name} className="rounded-sm border border-[#E6EDF2]">
              <div className="flex items-center bg-[#E6EDF2] px-8 min-h-12 ">
                <span className="font-bold">{t(`${LANG_CERTS_BASE}.${name}`)}</span>
              </div>
              <SingleCertificate
                testId={name}
                fieldName={name}
                uploadConditions={FILE_UPLOAD_PROFILES.certificates}
                files={filesByField[name] || []}
                setFiles={(next) => setFilesForKey(name, next as UploadEntry[])}
                removeFn={() => removeFile(name)}
                updateFormData={updateFormData}
              />
            </div>
          );
        })}
      </FormProvider>
    </div>
  );
};

export default VesselCertificates;
